/**
 * PrivateRoute — protects routes behind authentication + optional role check.
 * Also handles mustChangePassword redirect.
 *
 * Usage:
 *   <Route element={<PrivateRoute />}>            // any authenticated user
 *   <Route element={<PrivateRoute roles={["admin"]} />}>  // admin only
 */

import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthStore } from "@/stores/authStore";
import type { UserRole } from "@/types";

interface PrivateRouteProps {
  roles?: UserRole[];
}

export default function PrivateRoute({ roles }: PrivateRouteProps) {
  const { isAuthenticated } = useAuth();
  const { hasRole } = usePermissions();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const refreshCurrentUser = useAuthStore((s) => s.refreshCurrentUser);
  const [isRepairingSession, setIsRepairingSession] = useState(false);
  // After a logout the next account must not land on the previous user's page.
  const wasAuthenticated = useRef(false);
  if (isAuthenticated) wasAuthenticated.current = true;

  useEffect(() => {
    if (!isAuthenticated || user?.role) {
      setIsRepairingSession(false);
      return;
    }

    let cancelled = false;
    setIsRepairingSession(true);
    refreshCurrentUser().finally(() => {
      if (!cancelled) setIsRepairingSession(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, refreshCurrentUser, user?.role]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={wasAuthenticated.current ? undefined : { from: location }} replace />;
  }

  if (!user?.role || isRepairingSession) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Check if user must change password
  const mustChangePassword = user?.mustChangePassword;
  const isOnChangePasswordPage = location.pathname === "/change-password";

  // If must change password and not already on change-password page, redirect there
  if (mustChangePassword && !isOnChangePasswordPage) {
    return <Navigate to="/change-password" replace />;
  }

  // If on change-password page but doesn't need to change password, redirect to home
  if (isOnChangePasswordPage && !mustChangePassword) {
    return <Navigate to="/" replace />;
  }

  if (roles && !hasRole(...roles)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
